import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { motion, useIsPresent, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Maximize2,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { AnimatePresence } from '../../../components/ui/safe-animate-presence';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../../../components/ui/sheet';
import { cn } from 'lib/utils';
import type { FlowThemeSetting } from 'types';
import {
  DEFAULT_FLOW_THEME_ID,
  FLOW_THEMES_BY_CATEGORY,
  FLOW_THEME_CATEGORIES,
  FlowThemeErrorBoundary,
  getFlowThemeCardMotion,
  resolveFlowTheme,
  type FlowThemeCategoryId,
  type FlowThemeDefinition,
  type FlowThemeId,
} from '../../../components/QuestionFlow/themes';
import { FlowThemeBackdrop } from '../../../components/QuestionFlow/themes/FlowThemeBackdrop';
import { FlowThemePreview } from '../../../components/QuestionFlow/themes/FlowThemePreview';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';

/** What a flow with no theme of its own ('' = inherit) resolves to, from the admin API. */
export type FlowThemeInheritance = {
  /** The inherited theme id; unknown or missing values mean the default mountain. */
  theme?: string | null;
  /** 'course' when a course supplies the theme, otherwise 'default'. */
  source?: 'course' | 'default' | null;
  /** The course the theme comes from, when there is one. */
  courseName?: string | null;
  /** True when the flow's courses set different themes. */
  varies?: boolean;
};

export type FlowThemePickerProps = {
  /** The stored theme. In the flow variant '' (or no value) means "inherit from course". */
  value?: string | null;
  onChange: (theme: FlowThemeSetting) => void;
  disabled?: boolean;
  /**
   * 'flow' (default) adds an "Inherit from course" option. 'course' hides it and
   * words the picker for a course theme, which its flows inherit.
   */
  variant?: 'flow' | 'course';
  /** For the flow variant: what the inherit option currently resolves to. */
  inheritance?: FlowThemeInheritance | null;
  /**
   * The Life Plan theme is written for the Life Plan course's questions, so it is only offered for that course
   * and its modules. A course or module that already stores it keeps seeing it as the current value.
   */
  lifePlanAllowed?: boolean;
};

/** The theme that only belongs to the Life Plan course. */
export const LIFE_PLAN_THEME_ID: FlowThemeId = 'life_plan';

/** The inherit option's value, and the suffix of its `data-testid`. */
const INHERIT_VALUE = '' as const;
const INHERIT_TEST_ID = 'inherit';

const SAMPLE_STEPS = [
  { eyebrow: 'Welcome', title: 'Every step in this module moves the scene.', body: 'Use Next and Back to feel each transition.' },
  { eyebrow: 'Reflect', title: 'What would make today feel meaningful?', body: 'Learners answer here while the backdrop keeps evolving.' },
  { eyebrow: 'Notice', title: 'Name one thought that keeps coming back.', body: 'Tap anywhere on the background to send a ripple through it.' },
  { eyebrow: 'Choose', title: 'Which of these feels most true right now?', body: 'Card transitions follow the theme, not just the background.' },
  { eyebrow: 'Commit', title: 'What is one small step you will take this week?', body: 'Finishing the module plays the theme’s finale.' },
];

/**
 * Standard radio-group keys: arrows move with wrap-around, Home and End jump to
 * the first and last option. Returns the index to select, or null for other keys.
 */
const radioKeyTarget = (key: string, index: number, count: number): number | null => {
  if (count <= 0) {
    return null;
  }
  if (key === 'Home') {
    return 0;
  }
  if (key === 'End') {
    return count - 1;
  }
  const offset = ({ ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 } as Record<string, number>)[key];
  return offset ? (index + offset + count) % count : null;
};

const NEAR_VIEWPORT_MARGIN = '200px 0px';

/**
 * Whether an element is near the viewport now, and whether it ever has been.
 * Without IntersectionObserver (old browsers, jsdom) everything counts as visible.
 */
const useNearViewport = (ref: React.RefObject<Element>) => {
  const supported = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
  const [state, setState] = useState(() => ({ visible: !supported, seen: !supported }));
  useEffect(() => {
    const element = ref.current;
    if (!supported || !element) {
      return undefined;
    }
    const observer = new window.IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) {
        return;
      }
      setState((previous) => {
        const visible = entry.isIntersecting;
        const seen = previous.seen || visible;
        return previous.visible === visible && previous.seen === seen ? previous : { visible, seen };
      });
    }, { rootMargin: NEAR_VIEWPORT_MARGIN });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, supported]);
  return state;
};

const ThemeSwatch: React.FC<{ theme: FlowThemeDefinition; className?: string; testId?: string }> = ({
  theme,
  className,
  testId,
}) => (
  <div
    className={className}
    style={{ background: theme.swatch }}
    data-testid={`flow-theme-swatch-${testId ?? theme.id}`}
  />
);

/**
 * An option's live preview. Shows the static swatch until the option first
 * comes near the viewport, then keeps its canvas mounted (so a quick tap's
 * mousedown and mouseup land on the same node). Animates only while hovered or
 * focused, or while it is the selected option and on screen.
 */
const ThemeOptionPreview: React.FC<{
  theme: FlowThemeDefinition;
  isSelected: boolean;
  isActive: boolean;
  swatchTestId?: string;
}> = ({ theme, isSelected, isActive, swatchTestId }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const { visible, seen } = useNearViewport(containerRef);
  const className = 'aspect-[16/10] w-full';
  const swatch = <ThemeSwatch theme={theme} className={className} testId={swatchTestId} />;
  return (
    <span ref={containerRef} className="block w-full shrink-0">
      {seen ? (
        <FlowThemeErrorBoundary fallback={swatch}>
          <FlowThemePreview
            themeId={theme.id}
            animate={isActive || (isSelected && visible)}
            className={className}
          />
        </FlowThemeErrorBoundary>
      ) : swatch}
    </span>
  );
};

/** "Course default · Aurora", or "Default · Mountain" when no course sets a theme. */
export const describeInheritedTheme = (inheritance?: FlowThemeInheritance | null): string => {
  const theme = resolveFlowTheme(inheritance?.theme);
  return `${inheritance?.source === 'course' ? 'Course default' : 'Default'} · ${theme.label}`;
};

const describeInheritSource = (inheritance?: FlowThemeInheritance | null): string => {
  if (inheritance?.source !== 'course') {
    return 'No course sets a theme, so learners see the default.';
  }
  const from = inheritance.courseName ? `From ${inheritance.courseName}.` : 'From this module’s course.';
  return inheritance.varies
    ? `${from} Its courses set different themes, so learners see the theme of the course they open it from.`
    : from;
};

type PickerOption = {
  value: FlowThemeSetting;
  testId: string;
  theme: FlowThemeDefinition;
  label: string;
  caption?: string;
  description: string;
  isDefault?: boolean;
};

type CategoryFilter = 'all' | FlowThemeCategoryId;

const toThemeOption = (theme: FlowThemeDefinition): PickerOption => ({
  value: theme.id,
  testId: theme.id,
  theme,
  label: theme.label,
  description: theme.description,
  isDefault: theme.id === DEFAULT_FLOW_THEME_ID,
});

const optionMatches = (option: PickerOption, query: string, extra = ''): boolean =>
  !query || `${option.label} ${option.caption ?? ''} ${option.description} ${extra}`.toLowerCase().includes(query);

const categoryChips = (available: (theme: FlowThemeDefinition) => boolean): ReadonlyArray<{ id: CategoryFilter; label: string; count: number }> => [
  { id: 'all', label: 'All', count: FLOW_THEMES_BY_CATEGORY.filter(available).length },
  ...FLOW_THEME_CATEGORIES.map((group) => ({ id: group.id, label: group.label, count: group.themes.filter(available).length })),
];

/** "Default" chip: inline after a name, or (overlay) on a tile's thumbnail so long names keep their room. */
const DefaultBadge: React.FC<{ overlay?: boolean }> = ({ overlay = false }) => (
  <span
    className={cn(
      'shrink-0 rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium uppercase leading-none tracking-wide',
      overlay
        ? 'absolute left-1.5 top-1.5 bg-white/90 text-[0.5625rem] text-foreground shadow-sm [@container(min-width:44rem)]:text-[0.625rem]'
        : 'bg-muted text-muted-foreground',
    )}
  >
    Default
  </span>
);

const SelectedMark: React.FC = () => (
  <span
    className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-white text-[#FD4319] shadow-sm"
    aria-hidden="true"
  >
    <Check className="h-3 w-3" strokeWidth={3} />
  </span>
);

const SELECTED_RING = 'border-transparent ring-2 ring-[rgba(253,67,25,0.55)]';
const UNSELECTED_RING = 'border-border/60 hover:border-foreground/30';
/** Summary and inherit-row layout, sized by the picker's container (see the root's `container-type`). */
const SUMMARY_GRID = 'grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 [@container(min-width:22rem)]:grid-cols-[7rem_minmax(0,1fr)] [@container(min-width:22rem)]:gap-y-0.5 [@container(min-width:40rem)]:grid-cols-[8rem_minmax(0,1fr)]';
const SUMMARY_THUMBNAIL = 'relative block w-full overflow-hidden rounded-lg [@container(min-width:22rem)]:row-span-2';
const SUMMARY_HEADING = 'block min-w-0 [@container(min-width:22rem)]:self-end';
const SUMMARY_DETAIL = 'col-span-2 block min-w-0 [@container(min-width:22rem)]:col-span-1 [@container(min-width:22rem)]:col-start-2 [@container(min-width:22rem)]:self-start';
const OPTION_BASE ='overflow-hidden border bg-background text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Flow and course theme picker. Collapsed, it summarises the current choice
 * with "Change theme" and "Preview full screen"; expanded, it shows a compact,
 * filterable gallery of live previews grouped by category. Flow Builder uses
 * it with an "Inherit from course" option; the course editor without one.
 */
export const FlowThemePicker: React.FC<FlowThemePickerProps> = ({
  value,
  onChange,
  disabled = false,
  variant = 'flow',
  inheritance = null,
  lifePlanAllowed = false,
}) => {
  const isCourse = variant === 'course';
  const isAvailable = (theme: FlowThemeDefinition | FlowThemeId) => {
    const id = typeof theme === 'string' ? theme : theme.id;
    return id !== LIFE_PLAN_THEME_ID || lifePlanAllowed || value === LIFE_PLAN_THEME_ID;
  };
  const inheritOption = useMemo<PickerOption | null>(() => (isCourse ? null : {
    value: INHERIT_VALUE,
    testId: INHERIT_TEST_ID,
    theme: resolveFlowTheme(inheritance?.theme),
    label: 'Inherit from course',
    caption: describeInheritedTheme(inheritance),
    description: describeInheritSource(inheritance),
  }), [inheritance, isCourse]);

  const isInheriting = !isCourse && !value;
  const selectedValue: FlowThemeSetting = isInheriting ? INHERIT_VALUE : resolveFlowTheme(value).id;
  const previewTarget = isInheriting ? resolveFlowTheme(inheritance?.theme).id : resolveFlowTheme(value).id;
  const currentOption: PickerOption = isInheriting && inheritOption
    ? inheritOption
    : toThemeOption(resolveFlowTheme(value));

  const [expanded, setExpanded] = useState(false);
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [query, setQuery] = useState('');
  const [hoveredValue, setHoveredValue] = useState<FlowThemeSetting | null>(null);
  const [focusedValue, setFocusedValue] = useState<FlowThemeSetting | null>(null);
  const [summaryActive, setSummaryActive] = useState(false);
  const [previewId, setPreviewId] = useState<FlowThemeId | null>(null);
  const optionRefs = useRef<Partial<Record<string, HTMLButtonElement | null>>>({});
  const toggleRef = useRef<HTMLButtonElement>(null);
  const labelId = isCourse ? 'course-theme-label' : 'flow-theme-label';
  const chooserId = `${useId()}-theme-chooser`;

  const normalizedQuery = query.trim().toLowerCase();
  const visibleGroups = FLOW_THEME_CATEGORIES
    .filter((group) => category === 'all' || group.id === category)
    .map((group) => ({
      group,
      options: group.themes.filter(isAvailable).map(toThemeOption).filter((option) => optionMatches(option, normalizedQuery, group.label)),
    }))
    .filter(({ options }) => options.length > 0);
  // Inherit stays pinned above the gallery whatever the category, but a search can hide it.
  const showInherit = Boolean(inheritOption && optionMatches(inheritOption, normalizedQuery));
  const visibleOptions: PickerOption[] = [
    ...(showInherit && inheritOption ? [inheritOption] : []),
    ...visibleGroups.flatMap(({ options }) => options),
  ];
  // Roving tab stop: the selected option, or the first one shown when filters hide it.
  const tabStopValue = visibleOptions.some((option) => option.value === selectedValue)
    ? selectedValue
    : visibleOptions[0]?.value;

  const collapse = () => {
    setExpanded(false);
    setHoveredValue(null);
    setFocusedValue(null);
  };

  const collapseAndFocusToggle = () => {
    collapse();
    toggleRef.current?.focus();
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const target = radioKeyTarget(event.key, index, visibleOptions.length);
    if (target === null) {
      return;
    }
    event.preventDefault();
    const next = visibleOptions[target];
    onChange(next.value);
    optionRefs.current[next.value]?.focus();
  };

  const handleChooserKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') {
      return;
    }
    // Escape in a filled search box clears it first, as browsers do.
    if (event.target instanceof HTMLInputElement && event.target.value) {
      return;
    }
    event.preventDefault();
    collapseAndFocusToggle();
  };

  const radioProps = (option: PickerOption, index: number) => ({
    ref: (element: HTMLButtonElement | null) => {
      optionRefs.current[option.value] = element;
    },
    type: 'button' as const,
    role: 'radio',
    'aria-checked': option.value === selectedValue,
    tabIndex: option.value === tabStopValue ? 0 : -1,
    disabled,
    onClick: () => onChange(option.value),
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => handleOptionKeyDown(event, index),
    onMouseEnter: () => setHoveredValue(option.value),
    onMouseLeave: () => setHoveredValue((current) => (current === option.value ? null : current)),
    onFocus: () => setFocusedValue(option.value),
    onBlur: () => setFocusedValue((current) => (current === option.value ? null : current)),
    'data-testid': `flow-theme-option-${option.testId}`,
  });
  const isOptionActive = (option: PickerOption) => hoveredValue === option.value || focusedValue === option.value;
  const showGroupHeadings = visibleGroups.length > 1;
  let nextOptionIndex = showInherit ? 1 : 0;

  return (
    <div
      // A size container: the layout follows the picker's own width, which is
      // much narrower in Flow Builder on a phone than in the course editor.
      className="space-y-3 rounded-md border border-border/40 p-3 [container-type:inline-size]"
      data-testid={isCourse ? 'course-theme-picker' : 'flow-theme-picker'}
    >
      <div className="space-y-1">
        <Label id={labelId}>{isCourse ? 'Course theme' : 'Theme'}</Label>
        <p className="text-xs text-muted-foreground">
          {isCourse
            ? 'The animated background learners see in this course’s modules, unless a module picks its own. It evolves with every step and celebrates the finish.'
            : 'The animated background learners see. It evolves with every step and celebrates the finish. Inherit uses the course’s theme.'}
        </p>
      </div>

      <div
        className="flex flex-col gap-3 [@container(min-width:40rem)]:flex-row [@container(min-width:40rem)]:items-center"
        data-testid="flow-theme-summary"
      >
        {/* Narrow: a small thumbnail beside the name, the description below.
            Wider: the thumbnail spans both rows of text. */}
        <div className={cn(SUMMARY_GRID, 'min-w-0 flex-1')}>
          <div
            className={cn(SUMMARY_THUMBNAIL, 'border border-border/60')}
            onMouseEnter={() => setSummaryActive(true)}
            onMouseLeave={() => setSummaryActive(false)}
            data-testid="flow-theme-summary-thumbnail"
          >
            <ThemeOptionPreview
              theme={currentOption.theme}
              isSelected={false}
              isActive={summaryActive}
              swatchTestId="summary"
            />
          </div>
          <div className={cn(SUMMARY_HEADING, 'space-y-0.5')}>
            <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold leading-snug text-foreground" data-testid="flow-theme-summary-label">
              <span className="min-w-0 break-words">{currentOption.label}</span>
              {currentOption.isDefault ? <DefaultBadge /> : null}
            </p>
            {currentOption.caption ? (
              <p className="text-xs font-medium text-foreground" data-testid="flow-theme-summary-caption">
                {currentOption.caption}
              </p>
            ) : null}
          </div>
          <div className={cn(SUMMARY_DETAIL, 'space-y-0.5')}>
            <p className="line-clamp-2 text-xs leading-snug text-muted-foreground [@container(min-width:56rem)]:line-clamp-1" title={currentOption.description}>
              {currentOption.description}
            </p>
            {isCourse && !value ? (
              <p className="text-xs text-muted-foreground" data-testid="course-theme-unset">
                Not set: modules show the default, {resolveFlowTheme(DEFAULT_FLOW_THEME_ID).label}, until you pick a theme.
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 [@container(min-width:40rem)]:shrink-0 [@container(min-width:40rem)]:justify-end">
          <Button
            ref={toggleRef}
            type="button"
            variant="secondary"
            size="auto"
            className="px-4 py-2 text-xs"
            aria-expanded={expanded}
            aria-controls={chooserId}
            onClick={() => (expanded ? collapse() : setExpanded(true))}
            data-testid="flow-theme-picker-toggle"
          >
            {expanded ? 'Hide themes' : 'Change theme'}
            <ChevronDown aria-hidden="true" className={cn('transition-transform', expanded && 'rotate-180')} />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="auto"
            className="px-4 py-2 text-xs"
            onClick={() => setPreviewId(previewTarget)}
            data-testid="flow-theme-preview-open"
          >
            <Maximize2 aria-hidden="true" />
            Preview full screen
          </Button>
        </div>
      </div>

      <div
        id={chooserId}
        hidden={!expanded}
        className="space-y-4 border-t border-border/40 pt-3"
        onKeyDown={handleChooserKeyDown}
        data-testid="flow-theme-chooser"
      >
        {expanded ? (
          <>
            <div className="flex flex-col gap-2 [@container(min-width:56rem)]:flex-row [@container(min-width:56rem)]:items-center [@container(min-width:56rem)]:justify-between">
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Theme categories">
                {categoryChips(isAvailable).map((chip) => {
                  const isActive = category === chip.id;
                  return (
                    <Button
                      key={chip.id}
                      type="button"
                      variant={isActive ? 'default' : 'secondary'}
                      size="auto"
                      className="min-h-0 gap-1.5 px-2.5 py-1 text-[0.6875rem] shadow-none hover:shadow-none [@container(min-width:30rem)]:px-3 [@container(min-width:30rem)]:py-1.5 [@container(min-width:30rem)]:text-xs"
                      aria-pressed={isActive}
                      onClick={() => setCategory(chip.id)}
                      data-testid={`flow-theme-category-${chip.id}`}
                    >
                      {chip.label}
                      <span className={cn('tabular-nums', isActive ? 'text-white/70' : 'text-muted-foreground')}>
                        {chip.count}
                      </span>
                    </Button>
                  );
                })}
              </div>
              <div className="relative [@container(min-width:56rem)]:w-60">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  type="search"
                  size="sm"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search themes"
                  aria-label="Search themes"
                  className="bg-background pl-8"
                  data-testid="flow-theme-search"
                />
              </div>
            </div>

            <div role="radiogroup" aria-labelledby={labelId} className="space-y-4">
              {showInherit && inheritOption ? (
                <button
                  {...radioProps(inheritOption, 0)}
                  className={cn(
                    OPTION_BASE,
                    SUMMARY_GRID,
                    'w-full justify-start rounded-xl p-2',
                    inheritOption.value === selectedValue ? SELECTED_RING : UNSELECTED_RING,
                  )}
                >
                  <span className={SUMMARY_THUMBNAIL}>
                    <ThemeOptionPreview
                      theme={inheritOption.theme}
                      isSelected={inheritOption.value === selectedValue}
                      isActive={isOptionActive(inheritOption)}
                      swatchTestId={INHERIT_TEST_ID}
                    />
                    {inheritOption.value === selectedValue ? <SelectedMark /> : null}
                  </span>
                  <span className={cn(SUMMARY_HEADING, 'space-y-0.5')}>
                    <span className="block text-sm font-semibold text-foreground">{inheritOption.label}</span>
                    <span className="block text-xs font-medium text-foreground" data-testid="flow-theme-option-inherit-caption">
                      {inheritOption.caption}
                    </span>
                  </span>
                  <span className={cn(SUMMARY_DETAIL, 'px-1 pb-0.5 text-xs leading-snug text-muted-foreground [@container(min-width:22rem)]:p-0')}>
                    {inheritOption.description}
                  </span>
                </button>
              ) : null}
              {visibleGroups.map(({ group, options }) => (
                <div key={group.id} className="space-y-2">
                  {showGroupHeadings ? (
                    <p
                      className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground"
                      data-testid={`flow-theme-group-${group.id}`}
                    >
                      {group.label}
                    </p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2 [@container(min-width:30rem)]:grid-cols-3 [@container(min-width:44rem)]:grid-cols-5 [@container(min-width:44rem)]:gap-2.5">
                    {options.map((option) => {
                      const isSelected = option.value === selectedValue;
                      const index = nextOptionIndex;
                      nextOptionIndex += 1;
                      return (
                        <button
                          key={option.testId}
                          {...radioProps(option, index)}
                          title={option.description}
                          className={cn(
                            // Buttons centre their content vertically and grid rows stretch every
                            // tile to the tallest, so stack from the top: the preview stays flush
                            // with the tile's rounded top edge and the text follows it.
                            OPTION_BASE,
                            'flex min-w-0 flex-col items-stretch justify-start rounded-lg',
                            isSelected ? SELECTED_RING : UNSELECTED_RING,
                          )}
                        >
                          <span className="relative block">
                            <ThemeOptionPreview
                              theme={option.theme}
                              isSelected={isSelected}
                              isActive={isOptionActive(option)}
                            />
                            {option.isDefault ? <DefaultBadge overlay /> : null}
                            {isSelected ? <SelectedMark /> : null}
                          </span>
                          <span className="block min-w-0 px-2 py-1.5 [@container(min-width:44rem)]:px-2.5 [@container(min-width:44rem)]:py-2">
                            <span className="block truncate text-xs font-semibold leading-tight text-foreground [@container(min-width:44rem)]:text-[0.8125rem]">
                              {option.label}
                            </span>
                            <span className="mt-0.5 hidden truncate text-[0.6875rem] leading-snug text-muted-foreground [@container(min-width:56rem)]:block">
                              {option.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {visibleOptions.length === 0 ? (
              <div
                className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/60 px-4 py-6 text-center"
                data-testid="flow-theme-search-empty"
              >
                <p className="text-sm text-muted-foreground">No themes match “{query.trim()}”.</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="auto"
                  className="min-h-0 px-3 py-1.5 text-xs underline"
                  onClick={() => {
                    setQuery('');
                    setCategory('all');
                  }}
                >
                  Show all themes
                </Button>
              </div>
            ) : null}

            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                size="auto"
                className="px-4 py-2 text-xs"
                onClick={collapseAndFocusToggle}
                data-testid="flow-theme-picker-done"
              >
                Done
              </Button>
            </div>
          </>
        ) : null}
      </div>
      {previewId ? (
        <FlowThemePreviewDialog
          themeId={previewId}
          onThemeChange={setPreviewId}
          onClose={() => setPreviewId(null)}
          onUseTheme={disabled ? undefined : (theme) => {
            onChange(theme);
            setPreviewId(null);
          }}
          canUseTheme={isAvailable}
        />
      ) : null}
    </div>
  );
};

/** A card that is leaving the stage stays visible for its exit motion but can't be used or read out. */
const PreviewCardSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isPresent = useIsPresent();
  return (
    <div
      data-flow-surface="card"
      aria-hidden={isPresent ? undefined : true}
      className={cn(
        'rounded-3xl bg-white/60 px-6 py-7 text-foundation-base-darkbase-dark-500 shadow-xl sm:px-10',
        !isPresent && 'pointer-events-none',
      )}
    >
      {children}
    </div>
  );
};

const GLASS_PILL = 'bg-white/60 shadow-sm backdrop-blur-md';
const GLASS_ICON_BUTTON = 'grid h-8 w-8 shrink-0 place-items-center rounded-full text-foundation-base-darkbase-dark-500 transition hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The full-screen preview's "All themes" sheet: every theme as a thumbnail, grouped like the editor. */
const PreviewThemeGrid: React.FC<{
  currentId: FlowThemeId;
  onPick: (theme: FlowThemeId) => void;
}> = ({ currentId, onPick }) => {
  const [activeId, setActiveId] = useState<FlowThemeId | null>(null);
  return (
    <div className="space-y-5">
      {FLOW_THEME_CATEGORIES.map((group) => (
        <section key={group.id} className="space-y-2" aria-labelledby={`flow-theme-preview-grid-${group.id}`}>
          <h3
            id={`flow-theme-preview-grid-${group.id}`}
            className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {group.label}
          </h3>
          <div className="grid grid-cols-2 gap-2 min-[360px]:grid-cols-3">
            {group.themes.map((option) => {
              const isCurrent = option.id === currentId;
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-current={isCurrent ? 'true' : undefined}
                  data-current-theme={isCurrent ? 'true' : undefined}
                  onClick={() => onPick(option.id)}
                  onMouseEnter={() => setActiveId(option.id)}
                  onMouseLeave={() => setActiveId((current) => (current === option.id ? null : current))}
                  onFocus={() => setActiveId(option.id)}
                  onBlur={() => setActiveId((current) => (current === option.id ? null : current))}
                  data-testid={`flow-theme-preview-grid-option-${option.id}`}
                  className={cn(
                    OPTION_BASE,
                    'flex min-w-0 flex-col items-stretch justify-start rounded-lg',
                    isCurrent ? SELECTED_RING : UNSELECTED_RING,
                  )}
                >
                  <span className="relative block">
                    <ThemeOptionPreview theme={option} isSelected={isCurrent} isActive={activeId === option.id} />
                    {isCurrent ? <SelectedMark /> : null}
                  </span>
                  <span className="block truncate px-2 py-1.5 text-xs font-semibold text-foreground">{option.label}</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

type FlowThemePreviewDialogProps = {
  themeId: FlowThemeId;
  onThemeChange: (theme: FlowThemeId) => void;
  onClose: () => void;
  onUseTheme?: (theme: FlowThemeId) => void;
  /** Themes the preview may show but not apply here (the Life Plan theme outside its course). */
  canUseTheme?: (theme: FlowThemeId) => boolean;
};

export const FlowThemePreviewDialog: React.FC<FlowThemePreviewDialogProps> = ({ themeId, onThemeChange, onClose, onUseTheme, canUseTheme }) => {
  const theme = resolveFlowTheme(themeId);
  const total = SAMPLE_STEPS.length;
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [cycle, setCycle] = useState(0);
  const [gridOpen, setGridOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const allThemesRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useResponsiveLayout();
  const prefersReducedMotion = Boolean(useReducedMotion());
  const complete = step >= total;
  const progress = useMemo(() => ({ step, total, complete }), [complete, step, total]);
  const chromeTone = theme.chromeTone(step / total);
  const cardMotion = prefersReducedMotion ? null : getFlowThemeCardMotion(theme.id, isMobile);
  const sample = complete ? null : SAMPLE_STEPS[step];
  const themeCount = FLOW_THEMES_BY_CATEGORY.length;
  const themeIndex = Math.max(0, FLOW_THEMES_BY_CATEGORY.findIndex((option) => option.id === theme.id));

  const go = (next: number) => {
    setDirection(next > step ? 1 : -1);
    setStep(Math.max(0, Math.min(total, next)));
  };
  const restart = () => {
    setDirection(1);
    setStep(0);
    setCycle((value) => value + 1);
  };
  const switchTheme = (next: FlowThemeId) => {
    onThemeChange(next);
    restart();
  };
  const switchBy = (offset: number) => {
    switchTheme(FLOW_THEMES_BY_CATEGORY[(themeIndex + offset + themeCount) % themeCount].id);
  };
  // Arrow keys, Home and End anywhere in the switcher. Its buttons never unmount
  // or disable (the order wraps), so focus stays where it is.
  const handleSwitcherKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = radioKeyTarget(event.key, themeIndex, themeCount);
    if (target === null) {
      return;
    }
    event.preventDefault();
    switchTheme(FLOW_THEMES_BY_CATEGORY[target].id);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent
        hideCloseButton
        className="left-0 top-0 h-[100dvh] max-h-none w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none bg-transparent p-0 shadow-none sm:w-screen sm:p-0"
        data-testid="flow-theme-preview-dialog"
      >
        <div
          className="flow-theme-scope relative isolate flex h-full w-full flex-col"
          data-flow-theme={theme.id}
          data-flow-card-glass={theme.cardGlass}
          data-flow-chrome={chromeTone}
        >
          <FlowThemeErrorBoundary
            resetKey={theme.id}
            fallback={<div className="absolute inset-0 -z-10" style={{ background: theme.swatch }} aria-hidden="true" />}
          >
            <FlowThemeBackdrop key={`${theme.id}-${cycle}`} theme={theme} progress={progress} className="absolute inset-0 -z-10" />
          </FlowThemeErrorBoundary>

          {/* Below lg: title and close on the first row, the switcher and "Use" on
              the second. From lg up everything shares one row. */}
          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 pt-4 sm:px-8 sm:pt-6">
            <div className="min-w-0 flex-1">
              <DialogTitle className="flow-chrome-text truncate text-lg font-semibold text-foundation-base-darkbase-dark-500">
                {theme.label} theme preview
              </DialogTitle>
              <DialogDescription className="flow-chrome-muted text-xs text-foundation-base-darkbase-dark-500">
                Step {Math.min(step + 1, total)} of {total}{complete ? ' · complete' : ''}
              </DialogDescription>
            </div>
            <div className="order-last flex w-full min-w-0 items-center gap-2 lg:order-none lg:w-auto">
              <div
                role="group"
                aria-label="Preview theme"
                onKeyDown={handleSwitcherKeyDown}
                className={cn('flex min-w-0 flex-1 items-center gap-0.5 rounded-full p-1 sm:max-w-[26rem] lg:max-w-none lg:flex-none', GLASS_PILL)}
                data-testid="flow-theme-preview-switcher"
              >
                <button
                  type="button"
                  onClick={() => switchBy(-1)}
                  className={GLASS_ICON_BUTTON}
                  aria-label="Previous theme"
                  data-testid="flow-theme-preview-theme-prev"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <div
                  className="flex min-w-0 flex-1 items-baseline justify-center gap-1.5 px-1 text-foundation-base-darkbase-dark-500 lg:w-40 lg:flex-none"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span className="truncate text-xs font-semibold" data-testid="flow-theme-preview-name">{theme.label}</span>
                  <span className="shrink-0 text-[0.6875rem] tabular-nums opacity-70" aria-hidden="true" data-testid="flow-theme-preview-position">
                    {themeIndex + 1} / {themeCount}
                  </span>
                  <span className="sr-only">, theme {themeIndex + 1} of {themeCount}</span>
                </div>
                <button
                  type="button"
                  onClick={() => switchBy(1)}
                  className={GLASS_ICON_BUTTON}
                  aria-label="Next theme"
                  data-testid="flow-theme-preview-theme-next"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="mx-0.5 h-5 w-px shrink-0 bg-foundation-base-darkbase-dark-500/20" aria-hidden="true" />
                <Sheet open={gridOpen} onOpenChange={setGridOpen}>
                  <SheetTrigger asChild>
                    <button
                      ref={allThemesRef}
                      type="button"
                      className="flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-foundation-base-darkbase-dark-500 transition hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="All themes"
                      data-testid="flow-theme-preview-all"
                    >
                      <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline" aria-hidden="true">All themes</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    ref={gridRef}
                    side={isMobile ? 'bottom' : 'right'}
                    overlayClassName="bg-black/10 backdrop-blur-[2px]"
                    className={cn('flex-col gap-4 p-0', isMobile ? 'max-h-[80dvh]' : 'w-[28rem] max-w-[calc(100vw-2rem)]')}
                    onOpenAutoFocus={(event) => {
                      // Start on the theme being previewed rather than the close button.
                      const current = gridRef.current?.querySelector<HTMLButtonElement>('[data-current-theme="true"]');
                      if (current) {
                        event.preventDefault();
                        current.focus({ preventScroll: true });
                        current.scrollIntoView?.({ block: 'center' });
                      }
                    }}
                    onCloseAutoFocus={(event) => {
                      // Back to "All themes" (it stays mounted), even where a click doesn't focus buttons.
                      event.preventDefault();
                      allThemesRef.current?.focus({ preventScroll: true });
                    }}
                    data-testid="flow-theme-preview-grid"
                  >
                    <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border/40 bg-white/95 px-5 pb-3 pt-5 backdrop-blur">
                      <div className="space-y-0.5">
                        <SheetTitle className="text-base font-semibold text-foreground">All themes</SheetTitle>
                        <SheetDescription className="text-xs text-muted-foreground">
                          Pick one to preview it full screen.
                        </SheetDescription>
                      </div>
                      <SheetClose
                        className="rounded-full p-2 text-muted-foreground transition hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label="Close theme list"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </SheetClose>
                    </div>
                    <div className="px-5 pb-6">
                      <PreviewThemeGrid
                        currentId={theme.id}
                        onPick={(next) => {
                          switchTheme(next);
                          setGridOpen(false);
                        }}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              {onUseTheme && (canUseTheme?.(theme.id) ?? true) ? (
                <Button
                  type="button"
                  size="auto"
                  className="min-w-0 shrink px-4 py-2 text-xs"
                  onClick={() => onUseTheme(theme.id)}
                  data-testid="flow-theme-preview-use"
                >
                  <span className="truncate">Use {theme.label}</span>
                </Button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={cn('shrink-0 rounded-full p-2 text-foundation-base-darkbase-dark-500 transition hover:bg-white/80', GLASS_PILL)}
              aria-label="Close preview"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="relative grid min-h-0 flex-1 place-items-center overflow-hidden px-4">
              <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div
                  key={`${theme.id}-${cycle}-${step}`}
                  custom={direction}
                  variants={cardMotion?.variants ?? {
                    enter: { opacity: 0 },
                    center: { opacity: 1 },
                    exit: { opacity: 0 },
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={cardMotion?.transition ?? { duration: prefersReducedMotion ? 0 : 0.4 }}
                  style={cardMotion?.style}
                  className="col-start-1 row-start-1 w-full max-w-[34rem]"
                >
                  <PreviewCardSurface>
                    {sample ? (
                      <>
                        <p className="text-xs font-semibold uppercase tracking-wide text-foundation-base-darkbase-dark-300">
                          {sample.eyebrow}
                        </p>
                        <h3 className="mt-2 text-2xl font-normal leading-snug">{sample.title}</h3>
                        <p className="mt-3 text-sm text-foundation-base-darkbase-dark-400">{sample.body}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-semibold uppercase tracking-wide text-foundation-base-darkbase-dark-300">Finale</p>
                        <h3 className="mt-2 text-2xl font-normal leading-snug">Lesson complete</h3>
                        <p className="mt-3 text-sm text-foundation-base-darkbase-dark-400">
                          This is the moment learners see when they finish the module.
                        </p>
                      </>
                    )}
                  </PreviewCardSurface>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* The step controls stay outside the animated card so keyboard focus
                survives every step: the same two buttons persist, and Back is
                aria-disabled rather than disabled so it never drops focus. */}
            <div className="flex justify-center px-4 pb-6 pt-3 sm:pb-8">
              <div
                className="flex flex-wrap items-center gap-2 rounded-full bg-white/60 p-1.5 shadow-sm backdrop-blur-md"
                data-flow-surface="card"
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  className="px-4 py-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
                  onClick={() => {
                    if (step > 0) {
                      go(step - 1);
                    }
                  }}
                  aria-disabled={step === 0 ? true : undefined}
                  data-testid="flow-theme-preview-back"
                >
                  <ArrowLeft aria-hidden="true" /> Back
                </Button>
                <Button
                  type="button"
                  size="auto"
                  className="px-4 py-2"
                  onClick={complete ? restart : () => go(step + 1)}
                  data-testid={complete ? 'flow-theme-preview-restart' : 'flow-theme-preview-next'}
                >
                  {complete ? (
                    <>
                      <RotateCcw aria-hidden="true" /> Start over
                    </>
                  ) : (
                    <>
                      {step === total - 1 ? 'Finish' : 'Next'} <ArrowRight aria-hidden="true" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
